import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(currentLanguage === 'en' ?'Hello, I would like to inquire about your legal services.' :'Hujambo, ningependa kuuliza kuhusu huduma zenu za kisheria.');
    window.open(`https://wa.me/254721320787?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open('tel:+254721320787', '_self');
  };

  const content = {
    en: {
      headline: "Expert Legal Solutions for Your Business & Personal Needs",
      subheadline: "Peter & Partners Advocates LLP provides comprehensive legal services with over 15 years of combined experience in Kenyan law. We deliver personalized solutions that protect your interests and drive your success.",
      whatsappButton: "WhatsApp Consultation",
      callButton: "Call Now",
      trustBadge: "Trusted by 500+ Clients",
      experienceYears: "15+ Years Experience"
    },
    sw: {
      headline: "Suluhisho za Kisheria za Kitaalamu kwa Biashara na Mahitaji ya Binafsi",
      subheadline: "Peter & Partners Advocates LLP hutoa huduma kamili za kisheria na uzoefu wa zaidi ya miaka 15 katika sheria za Kenya. Tunatoa suluhisho za kibinafsi zinazolinda maslahi yako na kuongoza mafanikio yako.",
      whatsappButton: "Mazungumzo ya WhatsApp",
      callButton: "Piga Simu Sasa",
      trustBadge: "Tunaaminika na Wateja 500+",
      experienceYears: "Uzoefu wa Miaka 15+"
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="relative bg-gradient-to-br from-background via-surface/10 to-primary/5 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[600px] py-16 lg:py-24">
          {/* Content Column */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full">
                  <Icon name="Scale" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-primary">
                    {currentContent.experienceYears}
                  </span>
                </div>
                <div className="flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full">
                  <Icon name="Users" size={20} className="text-success" />
                  <span className="text-sm font-medium text-success">
                    {currentContent.trustBadge}
                  </span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-text-primary leading-tight">
                {currentContent.headline}
              </h1>

              <p className="text-lg md:text-xl text-text-secondary leading-relaxed max-w-2xl">
                {currentContent.subheadline}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                size="lg"
                onClick={handleWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                className="flex-1 sm:flex-none"
              >
                {currentContent.whatsappButton}
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={handleCallClick}
                iconName="Phone"
                iconPosition="left"
                className="flex-1 sm:flex-none"
              >
                {currentContent.callButton}
              </Button>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row gap-6 pt-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name="Phone" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">+254 721 320 787</p>
                  <p className="text-xs text-text-secondary">
                    {currentLanguage === 'en' ? 'Available 24/7' : 'Inapatikana 24/7'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                  <Icon name="Mail" size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">info@peterpartnerslaw.co.ke</p>
                  <p className="text-xs text-text-secondary">
                    {currentLanguage === 'en' ? 'Quick Response' : 'Majibu ya Haraka'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Image Column */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden">
              <Image
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Professional legal consultation at Peter & Partners Law Firm"
                className="w-full h-[500px] object-cover"
              />
              
              {/* Overlay Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary rounded-lg">
                    <Icon name="Award" size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-text-primary">
                      {currentLanguage === 'en' ? 'Award-Winning Legal Team' : 'Timu ya Kisheria Iliyoshinda Tuzo'}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {currentLanguage === 'en' ?'Recognized for excellence in legal practice' :'Imetambuliwa kwa ubora katika mazoezi ya kisheria'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-accent/20 rounded-full blur-xl"></div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-primary/20 rounded-full blur-xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;